// Chloe's page owns presentation and package intent. Releasit owns the real order.
const TARGET_PRODUCT_ID = '15444647412019';
const TARGET_DROPI_ID = '1866312';
const OWNER_SELECTOR = '[data-jogger-native-purchase] #_rsi-buy-now-button-overwrite';
const OPEN_MODAL_SELECTOR = '#_rsi-cod-form-modal._rsi-cod-form-modal-open';
const PACKAGE_PRICES = Object.freeze({1: 89900, 2: 129900, 3: 149900, 6: 249900});
const SIZES = Object.freeze(['S', 'M', 'L', 'XL', '2XL']);
const COLORS = Object.freeze(['Gris Oscuro', 'Blanco', 'Gris Claro', 'Azul Rey', 'Negro', 'Crema', 'Verde Militar', 'Verde Menta', 'Azul Oscuro', 'Azul Bebe', 'Palo De Rosa', 'Vinotinto', 'Lila', 'Beige']);
const BODY_OWNERS = new WeakMap();
const CONTROLLERS = new WeakMap();

export function normalizeNativeValue(value) {
  let text = String(value ?? '');
  try { text = decodeURIComponent(text); } catch { return ''; }
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().replace(/\s+/g, ' ').toLowerCase();
}

export function canonicalSize(value) {
  const size = normalizeNativeValue(value).toUpperCase();
  return size === 'XXL' ? '2XL' : size;
}

export function resolvePurchaseMode(config, renderedProductId) {
  if (String(renderedProductId) !== TARGET_PRODUCT_ID || String(config.productId) !== TARGET_PRODUCT_ID || String(config.dropiProductId) !== TARGET_DROPI_ID) throw Error('Chloe product identity mismatch');
  if (config.nativeCodConfigured === true && config.reviewOnly === false) return 'native';
  if (config.nativeCodConfigured === false && config.reviewOnly === true) return 'review';
  throw Error('Chloe purchase-mode flags must agree');
}

export function validateCatalog(config, mode) {
  const input = config.catalog;
  if (!input || !Array.isArray(input.variants) || input.variants.length !== 70) throw Error('Chloe requires the 70 real variants');
  if (input.shopifyProductId != null && String(input.shopifyProductId) !== TARGET_PRODUCT_ID) throw Error('Catalog Shopify product mismatch');
  if (input.dropiProductId != null && String(input.dropiProductId) !== TARGET_DROPI_ID) throw Error('Catalog Dropi product mismatch');
  const pairs = new Set(), shopifyIds = new Set(), supplierIds = new Set();
  const variants = input.variants.map(item => {
    const size = canonicalSize(item.size);
    const color = COLORS.find(value => normalizeNativeValue(value) === normalizeNativeValue(item.color));
    const variantId = item.variantId == null ? null : String(item.variantId);
    const dropiVariantId = String(item.dropiVariantId ?? '');
    if (!SIZES.includes(size) || !color || !/^\d+$/.test(dropiVariantId)) throw Error('Unsupported Chloe supplier combination');
    if (mode === 'native' && !/^\d+$/.test(variantId ?? '')) throw Error('Real Shopify variant ID required');
    if (variantId && shopifyIds.has(variantId) || supplierIds.has(dropiVariantId)) throw Error('Duplicate Chloe variant identity');
    const pair = size + '|' + color;
    if (pairs.has(pair)) throw Error('Duplicate Chloe size/color combination');
    if (item.price != null && Number(item.price) !== (size === '2XL' ? 9190000 : 8990000)) throw Error('Unexpected Chloe retail price in Shopify cents');
    if (item.barcode != null && String(item.barcode) !== TARGET_DROPI_ID + '-' + dropiVariantId) throw Error('Chloe supplier barcode mismatch');
    pairs.add(pair); if (variantId) shopifyIds.add(variantId); supplierIds.add(dropiVariantId);
    return {size, color, variantId, dropiVariantId, available: mode === 'native' ? item.available === true : item.available === true || (item.available == null && Number(item.stock) > 0)};
  });
  if (pairs.size !== 70 || SIZES.some(size => COLORS.some(color => !pairs.has(size + '|' + color)))) throw Error('Incomplete Chloe size/color matrix');
  return {shopifyProductId: TARGET_PRODUCT_ID, dropiProductId: TARGET_DROPI_ID, sizes: SIZES, colors: COLORS, variants};
}

export function calculateOrder(quantity, items) {
  if (!Number.isInteger(quantity) || !Object.hasOwn(PACKAGE_PRICES, quantity) || !Array.isArray(items) || items.length < quantity) throw Error('Invalid Chloe package');
  const selected = items.slice(0, quantity);
  if (selected.some(item => !item || !SIZES.includes(item.size) || !COLORS.includes(item.color))) throw Error('Invalid Chloe selection');
  const plusCount = selected.filter(item => item.size === '2XL').length;
  return {quantity, subtotal: PACKAGE_PRICES[quantity], plusCount, plusSurcharge: plusCount * 2000, shipping: 0, total: PACKAGE_PRICES[quantity] + plusCount * 2000};
}

function nativeContext(root, config) {
  if (!root.isConnected || resolvePurchaseMode(config, root.dataset.productId) !== 'native') throw Error('Chloe native context unavailable');
}

function visibleControl(element) {
  if (!element?.isConnected || element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
  const style = element.ownerDocument.defaultView.getComputedStyle(element);
  return style.display !== 'none' && style.visibility !== 'hidden' && style.visibility !== 'collapse' && element.getClientRects().length > 0;
}

export function installNativeScope(root, config) {
  nativeContext(root, config);
  const body = root.ownerDocument.body;
  const previous = BODY_OWNERS.get(body);
  if (previous && previous !== root && previous.isConnected) throw Error('Multiple Chloe native owners');
  BODY_OWNERS.set(body, root);
  body.setAttribute('data-cosautil-chloe-native', TARGET_PRODUCT_ID);
  return () => {
    if (BODY_OWNERS.get(body) !== root) return;
    BODY_OWNERS.delete(body);
    if (body.getAttribute('data-cosautil-chloe-native') === TARGET_PRODUCT_ID) body.removeAttribute('data-cosautil-chloe-native');
  };
}

function supportedOfferTitle(quantity, text) {
  const title = String(text ?? '').replace(/\s+/g, ' ').trim();
  return title === 'Pack de ' + quantity + (quantity === 1 ? ' jogger' : ' joggers') + ' · Envío gratis';
}

export function readPagePackage(root, config = {}) {
  const radios = [...root.querySelectorAll('quantity-breaks input[type="radio"][name="quantity"]')];
  if (config.pagePackageMode === 'native-only') {
    if (radios.length !== 0) throw Error('Native-only Chloe must not duplicate the app package controls');
    return {radios, quantity: 3};
  }
  if (config.pagePackageMode != null) throw Error('Unsupported Chloe page package mode');
  if (radios.length !== 4 || new Set(radios.map(radio => radio.value)).size !== 4 || radios.some(radio => !Object.hasOwn(PACKAGE_PRICES, radio.value))) throw Error('Expected the Chloe 1/2/3/6 package radios');
  const checked = radios.filter(radio => radio.checked);
  if (checked.length > 1) throw Error('Ambiguous Chloe page package');
  return {radios, quantity: checked.length === 1 ? Number(checked[0].value) : 3};
}

export function readNativeOfferIdentities(root) {
  const modals = [...root.ownerDocument.querySelectorAll(OPEN_MODAL_SELECTOR)];
  if (modals.length !== 1) return null;
  const modal = modals[0];
  const form = modal.querySelector('form#_rsi-cod-form-modal-form');
  const offers = form?.querySelector('#rsi-cod-form-quantity-offers-hook ._rsi-quantity-offers');
  if (!offers) return null;
  const containers = [...offers.querySelectorAll('div._rsi-quantity-offers-offer-container')];
  if (containers.length !== 4) return null;
  const quantities = new Set(), positions = new Set(), entries = [];
  for (const container of containers) {
    const titles = [...container.querySelectorAll('._rsi-quantity-offers-title')];
    if (titles.length !== 1) return null;
    const quantity = Object.keys(PACKAGE_PRICES).map(Number).find(value => supportedOfferTitle(value, titles[0].textContent));
    const rawPosition = container.getAttribute('data-offer-pos');
    if (!quantity || quantities.has(quantity) || !/^[0-3]$/.test(rawPosition ?? '') || positions.has(rawPosition)) return null;
    const clickTarget = container.querySelector(':scope > div._rsi-quantity-offers-offer');
    if (!clickTarget) return null;
    quantities.add(quantity); positions.add(rawPosition);
    entries.push({modal, form, offers, container, clickTarget, quantity, position: Number(rawPosition)});
  }
  return {modal, form, offers, entries};
}

export function readNativePackage(root, quantity) {
  if (!Number.isInteger(quantity) || !Object.hasOwn(PACKAGE_PRICES, quantity)) return null;
  return readNativeOfferIdentities(root)?.entries.find(entry => entry.quantity === quantity) || null;
}

export function readNativeSelection(root, config, catalog) {
  try { nativeContext(root, config); } catch { return null; }
  if (BODY_OWNERS.get(root.ownerDocument.body) !== root) return null;
  const selected = [...root.ownerDocument.querySelectorAll(OPEN_MODAL_SELECTOR + ' ._rsi-quantity-offers-offer-container._rsi-quantity-offers-selected')];
  if (selected.length !== 1) return null;
  const offer = readNativeOfferIdentities(root)?.entries.find(entry => entry.container === selected[0]);
  if (!offer) return null;
  const quantity = offer.quantity;
  const sizes = [...offer.container.querySelectorAll('select[data-offer-num-multi-sel][data-option-num="0"]')];
  const colors = [...offer.container.querySelectorAll('select[data-offer-num-multi-sel][data-option-num="1"]')];
  if (sizes.length !== quantity || colors.length !== quantity) return null;
  const items = [];
  for (let index = 1; index <= quantity; index++) {
    const sizeControls = sizes.filter(control => control.getAttribute('data-offer-num-multi-sel') === String(index));
    const colorControls = colors.filter(control => control.getAttribute('data-offer-num-multi-sel') === String(index));
    if (sizeControls.length !== 1 || colorControls.length !== 1) return null;
    const sizeControl = sizeControls[0], colorControl = colorControls[0];
    if (!visibleControl(sizeControl) || !visibleControl(colorControl) || sizeControl.disabled || colorControl.disabled || sizeControl.selectedOptions?.[0]?.disabled || colorControl.selectedOptions?.[0]?.disabled) return null;
    const size = canonicalSize(sizeControl.value), normalizedColor = normalizeNativeValue(colorControl.value);
    const matches = catalog.variants.filter(variant => variant.available && variant.size === size && normalizeNativeValue(variant.color) === normalizedColor);
    if (matches.length !== 1) return null;
    items.push({size, color: matches[0].color});
  }
  const totals = offer.modal.querySelectorAll('[data-checkout-line="total"][data-order-total]');
  if (totals.length !== 1) return null;
  const raw = totals[0].getAttribute('data-order-total');
  if (!/^\d+$/.test(raw ?? '')) return null;
  const cents = Number(raw), order = calculateOrder(quantity, items);
  if (!Number.isSafeInteger(cents) || cents !== order.total * 100) return null;
  return {...order, items, signature: JSON.stringify([quantity, cents, items.map(item => [item.size, item.color])])};
}

export function waitForNativeDom(root, config, read, timeoutMs = 10000, signal) {
  nativeContext(root, config);
  if (!Number.isFinite(timeoutMs) || timeoutMs < 1 || timeoutMs > 45000) throw Error('Invalid native wait bound');
  return new Promise((resolve, reject) => {
    let finished = false;
    const doc = root.ownerDocument, section = root.closest('.shopify-section');
    const observer = new MutationObserver(check);
    const timer = setTimeout(() => finish(Error('Native Chloe package could not be confirmed')), timeoutMs);
    const abort = () => finish(Error('Chloe synchronization cancelled'));
    function finish(error, value) {
      if (finished) return; finished = true;
      observer.disconnect(); clearTimeout(timer);
      doc.removeEventListener('change', check, true);
      section?.removeEventListener('shopify:section:unload', abort);
      signal?.removeEventListener('abort', abort);
      if (error) reject(error); else resolve(value);
    }
    function check() {
      try { nativeContext(root, config); if (signal?.aborted) { abort(); return; } const value = read(); if (value) finish(null, value); } catch (error) { finish(error); }
    }
    observer.observe(doc.body, {subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'data-order-total', 'disabled', 'selected', 'hidden'], characterData: true});
    doc.addEventListener('change', check, true);
    section?.addEventListener('shopify:section:unload', abort, {once: true});
    signal?.addEventListener('abort', abort, {once: true});
    check();
  });
}

export async function selectNativePackage(root, config, quantity, {timeoutMs = 10000, signal} = {}) {
  nativeContext(root, config);
  if (!Number.isInteger(quantity) || !Object.hasOwn(PACKAGE_PRICES, quantity)) throw Error('Unsupported Chloe package');
  root.dataset.nativeQuantitySync = 'pending';
  root.dataset.nativeRequestedQuantity = String(quantity);
  try {
    const offer = await waitForNativeDom(root, config, () => readNativePackage(root, quantity), timeoutMs, signal);
    if (!visibleControl(offer.clickTarget)) throw Error('Native Chloe offer is not visible');
    if (!offer.container.classList.contains('_rsi-quantity-offers-selected')) offer.clickTarget.click();
    await waitForNativeDom(root, config, () => {
      const current = readNativePackage(root, quantity);
      if (!current || !current.container.classList.contains('_rsi-quantity-offers-selected')) return null;
      const selected = [...current.offers.querySelectorAll('div._rsi-quantity-offers-offer-container._rsi-quantity-offers-selected')];
      if (selected.length !== 1 || selected[0] !== current.container) return null;
      if (current.container.querySelectorAll('select[data-offer-num-multi-sel][data-option-num="0"]').length !== quantity || current.container.querySelectorAll('select[data-offer-num-multi-sel][data-option-num="1"]').length !== quantity) return null;
      return current;
    }, timeoutMs, signal);
    root.dataset.nativeQuantitySync = 'verified-on-open';
  } catch (error) { root.dataset.nativeQuantitySync = 'failed'; throw error; }
}

export async function restoreNativeSelection(root, config, catalog, quantity, items, {timeoutMs = 10000, signal} = {}) {
  nativeContext(root, config);
  if (!Number.isInteger(quantity) || !Object.hasOwn(PACKAGE_PRICES, quantity) || !Array.isArray(items) || items.length !== quantity) throw Error('Invalid retained Chloe choices');
  if (!Number.isFinite(timeoutMs) || timeoutMs < 1 || timeoutMs > 10000) throw Error('Invalid native wait bound');
  // Validate the entire remembered pack before changing a single visible select.
  if (items.some(item => !catalog.variants.some(variant => variant.available && variant.size === item.size && variant.color === item.color))) throw Error('A remembered Chloe combination is unavailable');
  const deadline = Date.now() + timeoutMs;
  const wait = read => { const remaining = deadline - Date.now(); if (remaining < 1) throw Error('Retained Chloe choices timed out'); return waitForNativeDom(root, config, read, remaining, signal); };
  const currentControl = (index, option) => {
    const offer = readNativePackage(root, quantity);
    if (!offer?.container.classList.contains('_rsi-quantity-offers-selected')) return null;
    const controls = offer.container.querySelectorAll('select[data-offer-num-multi-sel="' + index + '"][data-option-num="' + option + '"]');
    return controls.length === 1 && !controls[0].disabled && visibleControl(controls[0]) ? controls[0] : null;
  };
  const pick = async (index, option, wanted, normalize) => {
    const target = await wait(() => {
      const control = currentControl(index, option); if (!control) return null;
      const options = [...control.options].filter(item => !item.disabled && normalize(item.value) === normalize(wanted));
      return options.length === 1 ? {control, option: options[0]} : null;
    });
    if (target.control.value !== target.option.value) {
      // This is the native visible select's ordinary user-change contract.
      target.control.value = target.option.value;
      target.control.dispatchEvent(new root.ownerDocument.defaultView.Event('change', {bubbles: true}));
    }
    await wait(() => { const control = currentControl(index, option); return control && normalize(control.value) === normalize(wanted); });
  };
  for (let index = 1; index <= quantity; index++) {
    await pick(index, 0, items[index - 1].size, canonicalSize);
    await pick(index, 1, items[index - 1].color, normalizeNativeValue);
  }
  return wait(() => {
    const snapshot = readNativeSelection(root, config, catalog);
    return snapshot && snapshot.quantity === quantity && snapshot.items.every((item, index) => item.size === items[index].size && item.color === items[index].color) ? snapshot : null;
  });
}

export function observeNativeSelection(root, config, catalog, {isOpening, onSelection, signal} = {}) {
  nativeContext(root, config);
  const doc = root.ownerDocument, section = root.closest('.shopify-section');
  let stopped = false, queued = false, forceNext = false, lastSignature = null;
  function refresh(force = false) {
    forceNext = forceNext || force;
    if (stopped || queued) return;
    queued = true;
    queueMicrotask(() => {
      queued = false;
      if (stopped) return;
      if (!root.isConnected || signal?.aborted) { cleanup(); return; }
      if (isOpening?.()) return;
      const snapshot = readNativeSelection(root, config, catalog);
      if (!snapshot) return; // A closed modal must never overwrite a newer page choice.
      const force = forceNext; forceNext = false;
      if (force || snapshot.signature !== lastSignature) { lastSignature = snapshot.signature; onSelection(snapshot); }
    });
  }
  const observer = new MutationObserver(() => refresh());
  observer.observe(doc.body, {subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'data-order-total', 'disabled', 'selected', 'hidden'], characterData: true});
  const onChange = event => { if (event.target?.closest?.(OPEN_MODAL_SELECTOR)) refresh(); };
  doc.addEventListener('change', onChange, true);
  function cleanup() {
    if (stopped) return; stopped = true;
    observer.disconnect(); doc.removeEventListener('change', onChange, true);
    section?.removeEventListener('shopify:section:unload', cleanup);
    signal?.removeEventListener('abort', cleanup);
  }
  section?.addEventListener('shopify:section:unload', cleanup, {once: true});
  signal?.addEventListener('abort', cleanup, {once: true});
  return {refresh, cleanup};
}

function applyPresentation(element, properties) {
  const expected = element.ownerDocument.createElement('span').style;
  for (const [property, value] of Object.entries(properties)) expected.setProperty(property, value, 'important');
  for (const property of Object.keys(properties)) {
    const value = expected.getPropertyValue(property);
    if (element.style.getPropertyValue(property) !== value || element.style.getPropertyPriority(property) !== 'important') element.style.setProperty(property, value, 'important');
  }
}

export function clearNativeOfferPresentation(root) {
  if (BODY_OWNERS.get(root.ownerDocument.body) !== root) return;
  root.ownerDocument.querySelectorAll('#_rsi-cod-form-modal [data-chloe-presentation-owner="' + TARGET_PRODUCT_ID + '"]').forEach(node => node.remove());
}

// Releasit's public product-only close flow clears the temporary checkout cart,
// then restores the customer's original cart asynchronously. Observe completion
// and verify the documented read-only Shopify cart before allowing another open.
export function installNativeCloseFence(root, signal, options = {}) {
  const doc = root.ownerDocument, view = doc.defaultView;
  const clock = options.now || (() => view.performance.now());
  const later = options.setTimer || ((callback, ms) => view.setTimeout(callback, ms));
  const cancel = options.clearTimer || (timer => view.clearTimeout(timer));
  const PO = options.PerformanceObserver || view.PerformanceObserver;
  const MO = options.MutationObserver || view.MutationObserver;
  const observerEntries = [], waiters = new Set();
  let phase = 'baseline', baseline = null, activeModal = null, closeFloor = 0, closedAt = 0, closeIntent = null;
  let stopped = false, hardFailure = false, generation = 0, pollTimer = null, deadlineTimer = null, intentTimer = null, requestAbort = null, inFlight = false;
  let resourceObserver = null, domObserver = null, successfulClear = null, activeProofAbort = null, activeNonempty = false, closingActiveNonempty = false, openingEpoch = 0;
  const publicMessage = 'Vuelve a cargar la página para continuar.';
  const cartUrl = new URL('cart.js', new URL(view.Shopify?.routes?.root || '/', doc.baseURI));
  const origin = new URL(doc.baseURI).origin;
  function fingerprint(cart) {
    if (!cart || !Array.isArray(cart.items)) throw Error('Invalid public cart response');
    const sums = new Map();
    for (const item of cart.items) {
      const id = String(item.variant_id ?? item.id ?? ''), quantity = Number(item.quantity);
      if (!/^\d+$/.test(id) || !Number.isSafeInteger(quantity) || quantity < 1) throw Error('Invalid public cart item identity');
      sums.set(id, (sums.get(id) || 0) + quantity);
    }
    return JSON.stringify([...sums].sort(([a], [b]) => a.localeCompare(b)));
  }
  const readCart = options.readCart || (async requestSignal => {
    const url = new URL(cartUrl.href); url.searchParams.set('_chloe_readback', String(Date.now()));
    const response = await view.fetch(url.href, {method: 'GET', credentials: 'same-origin', cache: 'no-store', signal: requestSignal});
    if (!response.ok) throw Error('Public cart read failed');
    return response.json();
  });
  function reason(code) { root.dataset.nativeCloseRestoreReason = code; }
  function change(next, code = next) { phase = next; root.dataset.nativeCloseRestore = next; reason(code); options.onState?.(next); }
  function clearTimers() { if (pollTimer != null) cancel(pollTimer); if (deadlineTimer != null) cancel(deadlineTimer); if (intentTimer != null) cancel(intentTimer); pollTimer = deadlineTimer = intentTimer = null; }
  function settle(error) { for (const waiter of waiters) error ? waiter.reject(error) : waiter.resolve(); waiters.clear(); }
  function fail(code = 'readiness-timeout') { if (stopped) return; generation++; requestAbort?.abort(); inFlight = false; clearTimers(); change('failed', code); settle(Error(publicMessage)); }
  function ready() { const completed = phase === 'closing' ? 'restored-cart-verified' : 'original-cart-recorded'; clearTimers(); change('ready', completed); settle(); }
  async function request() {
    const token = generation; requestAbort = new AbortController(); inFlight = true;
    try {
      const value = fingerprint(await readCart(requestAbort.signal));
      if (stopped || token !== generation || !['baseline', 'closing'].includes(phase)) return;
      if (baseline == null && phase === 'baseline') { baseline = value; ready(); }
      else if (phase === 'closing' && successfulClear && value === baseline) ready();
      else { reason('cart-not-restored'); pollTimer = later(poll, 400); }
    } catch (error) {
      if (!stopped && token === generation) { reason('public-cart-read-error'); pollTimer = later(phase === 'baseline' ? startBaseline : poll, 800); }
    } finally { if (token === generation) inFlight = false; }
  }
  function successfulCloseClear(entry) {
    let url; try { url = new URL(entry.name, doc.baseURI); } catch { return false; }
    return url.origin === origin && /\/cart\/clear\.js$/.test(url.pathname) && Number(entry.startTime) >= closeFloor && Number(entry.responseEnd) >= closedAt && Number(entry.responseEnd) > Number(entry.startTime);
  }
  const status = entry => entry.responseStatus == null ? null : Number(entry.responseStatus);
  const okStatus = entry => status(entry) >= 200 && status(entry) < 300;
  function compatibilityProof(clear) {
    if (status(clear) != null) return false;
    if (baseline === '[]') return closingActiveNonempty;
    return observerEntries.some(entry => {
      let url; try { url = new URL(entry.name, doc.baseURI); } catch { return false; }
      return url.origin === origin && /\/cart\/add\.js$/.test(url.pathname) && Number(entry.startTime) >= Number(clear.responseEnd) && Number(entry.responseEnd) > Number(entry.startTime) && (status(entry) == null || okStatus(entry));
    });
  }
  function ingest(entries) {
    for (const entry of entries) {
      let url; try { url = new URL(entry.name, doc.baseURI); } catch { continue; }
      if (url.origin === origin && /\/cart\/(?:clear|add|update)\.js$/.test(url.pathname)) observerEntries.push(entry);
    }
    if (observerEntries.length > 120) observerEntries.splice(0, observerEntries.length - 120);
    if (phase === 'closing') poll();
  }
  function poll() {
    if (stopped || phase !== 'closing' || inFlight) return;
    resourceObserver?.takeRecords && ingestWithoutPoll(resourceObserver.takeRecords());
    successfulClear ||= observerEntries.find(entry => successfulCloseClear(entry) && (okStatus(entry) || compatibilityProof(entry))) || null;
    if (successfulClear) { reason('close-clear-completed'); void request(); }
  }
  function ingestWithoutPoll(entries) { for (const entry of entries) observerEntries.push(entry); }
  function armDeadline(ms) { if (deadlineTimer != null) cancel(deadlineTimer); const code = phase === 'baseline' ? 'original-cart-timeout' : 'close-restore-timeout'; deadlineTimer = later(() => fail(code), ms); }
  function startBaseline() {
    if (stopped || baseline != null || inFlight) return;
    change('baseline'); if (deadlineTimer == null) armDeadline(15000); void request();
  }
  function closed() {
    if (stopped || phase !== 'open') return;
    generation++; activeProofAbort?.abort(); closingActiveNonempty = activeNonempty; successfulClear = null; closedAt = clock();
    if (!closeIntent || closeIntent.modal !== activeModal) { hardFailure = true; fail('close-unattributed'); return; }
    closeFloor = closeIntent.at; closeIntent = null; change('closing', 'awaiting-close-clear'); armDeadline(30000); poll();
  }
  function inspectModal() {
    if (phase === 'open' && activeModal && (!activeModal.isConnected || !activeModal.matches(OPEN_MODAL_SELECTOR))) closed();
  }
  function noteCloseIntent(event) {
    if (phase !== 'open' || !activeModal?.matches(OPEN_MODAL_SELECTOR)) return;
    const close = event.target?.closest?.('#_rsi-cod-form-modal-close');
    if ((event.type === 'click' && (close && activeModal.contains(close) || event.target === activeModal)) || event.type === 'keydown' && event.key === 'Escape') {
      const intent = {modal: activeModal, at: clock()}; closeIntent = intent;
      if (intentTimer != null) cancel(intentTimer);
      // A trusted browser event can run microtasks between capture and target
      // listeners. Only the next task is after the app's close handler.
      intentTimer = later(() => { intentTimer = null; if (closeIntent === intent && activeModal?.matches(OPEN_MODAL_SELECTOR)) closeIntent = null; }, 0);
    }
  }
  try {
    if (!PO || !MO) throw Error('Public completion observation unavailable');
    resourceObserver = new PO(list => ingest(list.getEntries()));
    try { resourceObserver.observe({type: 'resource', buffered: true}); } catch { resourceObserver.observe({entryTypes: ['resource']}); }
    domObserver = new MO(inspectModal); domObserver.observe(doc.body, {subtree: true, childList: true, attributes: true, attributeFilter: ['class']});
    view.addEventListener('click', noteCloseIntent, true); view.addEventListener('keydown', noteCloseIntent, true);
    startBaseline();
  } catch { hardFailure = true; fail('completion-observer-unavailable'); }
  const controller = {
    blocked() { inspectModal(); return ['baseline', 'closing', 'failed'].includes(phase); },
    state: () => phase,
    wait() {
      if (stopped || hardFailure) return Promise.reject(Error(publicMessage));
      if (!controller.blocked()) return Promise.resolve();
      const promise = new Promise((resolve, reject) => waiters.add({resolve, reject}));
      if (phase === 'failed') { generation++; if (baseline == null) startBaseline(); else { change('closing'); armDeadline(30000); poll(); } }
      return promise;
    },
    opening() { if (controller.blocked()) throw Error(publicMessage); closeIntent = null; change('opening'); },
    openingFailed() { hardFailure = true; fail('native-opening-unverified'); },
    verified(modal) {
      if (stopped || hardFailure || !modal || !['opening', 'open'].includes(phase)) return;
      const fresh = phase === 'opening' || activeModal !== modal; activeModal = modal; change('open');
      if (fresh) {
        activeNonempty = false; const token = generation, epoch = ++openingEpoch;
        if (baseline === '[]') {
          activeProofAbort?.abort(); activeProofAbort = new AbortController();
          void readCart(activeProofAbort.signal).then(cart => { if (!stopped && token === generation && epoch === openingEpoch && phase === 'open' && !closeIntent && activeModal === modal) activeNonempty = fingerprint(cart) !== '[]'; }).catch(() => {});
        }
      }
    },
    cleanup() { if (stopped) return; stopped = true; generation++; requestAbort?.abort(); activeProofAbort?.abort(); clearTimers(); resourceObserver?.disconnect(); domObserver?.disconnect(); view.removeEventListener('click', noteCloseIntent, true); view.removeEventListener('keydown', noteCloseIntent, true); settle(Error(publicMessage)); signal?.removeEventListener('abort', controller.cleanup); }
  };
  signal?.addEventListener('abort', controller.cleanup, {once: true});
  if (signal?.aborted) controller.cleanup();
  return controller;
}

export function refreshNativeOfferPresentation(root) {
  const doc = root.ownerDocument, marker = 'data-chloe-presentation-owner';
  if (BODY_OWNERS.get(doc.body) !== root || !root.isConnected) return null;
  const modals = [...doc.querySelectorAll(OPEN_MODAL_SELECTOR)];
  const clear = () => modals.forEach(modal => modal.querySelectorAll('[' + marker + '="' + TARGET_PRODUCT_ID + '"]').forEach(node => node.remove()));
  const identities = readNativeOfferIdentities(root);
  if (!identities) { clear(); return null; }
  const {modal, form} = identities;
  const header = form.querySelector(':scope > ._rsi-modal-header'), hook = form.querySelector(':scope > #rsi-cod-form-quantity-offers-hook');
  const cards = identities.entries.map(entry => ({...entry, info: entry.clickTarget.querySelector('._rsi-quantity-offers-info'), title: entry.container.querySelector('._rsi-quantity-offers-title')}));
  if (!header || !hook || header.parentElement !== form || hook.parentElement !== form || cards.some(card => !card.info || !card.title || !card.info.contains(card.title))) { clear(); return null; }
  function ensure(parent, tag, className, text) {
    const nodes = [...parent.children].filter(node => node.classList.contains(className) && node.getAttribute(marker) === TARGET_PRODUCT_ID);
    let node = nodes.shift();
    nodes.forEach(extra => extra.remove());
    if (node && node.tagName !== tag.toUpperCase()) { node.remove(); node = null; }
    if (!node) { node = doc.createElement(tag); node.className = className; node.setAttribute(marker, TARGET_PRODUCT_ID); parent.append(node); }
    if (text != null && node.textContent !== text) node.textContent = text;
    return node;
  }
  const delivery = ensure(form, 'aside', 'chloe-cod-delivery');
  if (delivery.getAttribute('aria-label') !== 'Tiempos de envío estimados') delivery.setAttribute('aria-label', 'Tiempos de envío estimados');
  ensure(delivery, 'span', 'chloe-cod-delivery__eyebrow', 'ENVÍO RÁPIDO');
  const grid = ensure(delivery, 'div', 'chloe-cod-delivery__grid');
  for (const [key, amount, title, qualifier] of [['dispatch', '24 h', 'Despacho estimado', 'Tras confirmar tu pedido'], ['arrival', '3 días', 'Entrega estimada', 'Después del despacho']]) {
    const column = ensure(grid, 'div', 'chloe-cod-delivery__' + key);
    ensure(column, 'strong', 'chloe-cod-delivery__time', amount);
    ensure(column, 'b', 'chloe-cod-delivery__label', title);
    ensure(column, 'span', 'chloe-cod-delivery__qualifier', qualifier);
  }
  ensure(delivery, 'p', 'chloe-cod-delivery__note', 'Los tiempos estimados pueden variar según el destino y la transportadora. Te confirmamos la cobertura antes del despacho.');
  if (header.nextElementSibling !== delivery) form.insertBefore(delivery, header.nextSibling);
  const heading = ensure(form, 'section', 'chloe-cod-offer-heading');
  ensure(heading, 'span', 'chloe-cod-offer-heading__eyebrow', 'ARMA TUS LOOKS');
  ensure(heading, 'h2', 'chloe-cod-offer-heading__title', 'Elige tu pack de joggers');
  ensure(heading, 'p', 'chloe-cod-offer-heading__description', 'Combina tallas y colores · Envío gratis');
  ensure(heading, 'small', 'chloe-cod-size-note', 'Precios S–XL · Cada prenda 2XL suma $2.000.');
  if (heading.nextElementSibling !== hook) form.insertBefore(heading, hook);
  const subtitles = {1: 'Tu nuevo favorito', 2: 'Más looks para tu semana', 3: 'Un look para cada día', 6: 'Para combinar y compartir'};
  const benefits = {1: 'Envío gratis · Pagas al recibir', 2: 'Ahorra $49.900 · Envío gratis', 3: 'Ahorra $119.800 · Envío gratis', 6: 'Ahorra $289.500 · Envío gratis'};
  for (const {container, info, quantity} of cards) {
    const copy = ensure(info, 'div', 'chloe-cod-pack-copy');
    ensure(copy, 'h3', 'chloe-cod-pack-title', quantity + (quantity === 1 ? ' JOGGER' : ' JOGGERS'));
    ensure(copy, 'p', 'chloe-cod-pack-subtitle', subtitles[quantity]);
    ensure(info, 'p', 'chloe-cod-pack-benefit', benefits[quantity]);
    if (quantity === 3) {
      const badge = ensure(container, 'div', 'chloe-cod-recommended-tag', 'PACK RECOMENDADO');
      if (container.firstElementChild !== badge) container.insertBefore(badge, container.firstChild);
    }
  }
  return {modal, form, delivery, heading, cards: cards.map(card => card.container)};
}

function refreshNativePresentation(root, config) {
  if (BODY_OWNERS.get(root.ownerDocument.body) !== root || !root.isConnected) return;
  const owners = [...root.querySelectorAll(OWNER_SELECTOR)];
  if (owners.length === 1) {
    const owner = owners[0];
    applyPresentation(owner, {'background': '#7b4148', 'background-color': '#7b4148', 'color': '#fffaf7', 'font-family': 'Poppins, sans-serif', 'font-size': 'clamp(16px, 4.6vw, 19px)', 'font-weight': '700', 'border': '1px solid #67333b', 'border-radius': '12px', 'width': '100%', 'box-sizing': 'border-box', 'display': 'flex', 'align-items': 'center', 'justify-content': 'center', 'gap': '12px', 'min-height': '78px', 'margin': '0px', 'padding': '13px 14px', 'box-shadow': '0 4px 12px #7b414826', 'line-height': '1.45', 'letter-spacing': 'normal', 'text-align': 'center', 'text-transform': 'none'});
    const copy = [...owner.children].find(node => node.tagName === 'SPAN');
    if (copy) {
      const texts = [...copy.childNodes].filter(node => node.nodeType === 3 && node.textContent.trim());
      if (texts.length === 1 && texts[0].nodeValue !== 'COMPRAR Y PAGAR AL RECIBIR') texts[0].nodeValue = 'COMPRAR Y PAGAR AL RECIBIR';
      applyPresentation(copy, {'font-family': 'Poppins, sans-serif', 'font-size': 'clamp(16px, 4.6vw, 19px)', 'font-weight': '700', 'line-height': '1.45', 'white-space': 'normal', 'min-width': '0px', 'flex': '1 1 auto'});
      const subtitle = copy.querySelector('._rsi-buy-now-button-subtitle');
      if (subtitle) {
        if (subtitle.textContent !== 'Aca eliges tallas y colores') subtitle.textContent = 'Aca eliges tallas y colores';
        applyPresentation(subtitle, {'display': 'block', 'color': '#fffaf7', 'font-family': 'Poppins, sans-serif', 'font-size': 'clamp(14px, 4vw, 16px)', 'font-weight': '400', 'line-height': '1.5', 'white-space': 'normal'});
      }
    }
    if (!owner.matches('button,a,input')) {
      if (!owner.hasAttribute('role')) owner.setAttribute('role', 'button');
      if (!owner.hasAttribute('tabindex')) owner.setAttribute('tabindex', '0');
      if (!owner.hasAttribute('aria-haspopup')) owner.setAttribute('aria-haspopup', 'dialog');
    }
  }
  const modals = [...root.ownerDocument.querySelectorAll(OPEN_MODAL_SELECTOR)];
  if (modals.length !== 1) return;
  const modal = modals[0], form = modal.querySelector('form#_rsi-cod-form-modal-form');
  if (!form) return;
  const identities = readNativeOfferIdentities(root);
  if (identities) {
    for (const {container, quantity} of identities.entries) {
      if (container.getAttribute('data-chloe-quantity') !== String(quantity)) container.setAttribute('data-chloe-quantity', String(quantity));
    }
  } else {
    modal.querySelectorAll('._rsi-quantity-offers-offer-container[data-chloe-quantity]').forEach(container => container.removeAttribute('data-chloe-quantity'));
  }
  refreshNativeOfferPresentation(root);
  const heading = modal.querySelector('._rsi-modal-header-title');
  if (heading) applyPresentation(heading, {'color': '#49342e', 'font-family': 'Poppins, sans-serif', 'font-size': '13px', 'font-weight': '700', 'line-height': '1.4', 'text-align': 'left', 'letter-spacing': '.045em'});
  const close = modal.querySelector('._rsi-modal-close-button');
  if (close && !close.getAttribute('aria-label')?.trim() && !close.getAttribute('aria-labelledby')?.trim()) close.setAttribute('aria-label', 'Cerrar formulario');
  if (config.formIntroUrl && !form.querySelector('.chloe-cod-intro')) {
    let url;
    try { url = new URL(config.formIntroUrl, root.ownerDocument.baseURI); } catch { url = null; }
    if (url && /^https?:$/.test(url.protocol)) {
      const image = root.ownerDocument.createElement('img');
      image.className = 'chloe-cod-intro'; image.src = url.href;
      image.alt = 'INGRESA TUS DATOS EXACTOS. Agilizará que tu pedido llegue más rápido';
      image.width = 1016; image.height = 125; form.prepend(image);
    }
  }
  const buttons = [...modal.querySelectorAll('button._rsi-modal-submit-button')];
  if (buttons.length === 1) {
    const button = buttons[0];
    applyPresentation(button, {'background': '#7b4148', 'background-color': '#7b4148', 'color': '#fffaf7', 'font-family': 'Poppins, sans-serif', 'font-size': '18px', 'font-weight': '700', 'border': '1px solid #67333b', 'border-radius': '12px', 'box-shadow': '0 4px 12px #7b414826'});
    const copy = [...button.children].find(node => node.tagName === 'SPAN' && node.querySelector('.rsi-cod-form-submit-total-value'));
    if (copy) {
      const texts = [...copy.childNodes].filter(node => node.nodeType === 3 && node.textContent.trim());
      if (texts.length === 1 && texts[0].nodeValue !== 'CONFIRMAR PEDIDO ') texts[0].nodeValue = 'CONFIRMAR PEDIDO ';
      if (!button.querySelector('.chloe-cod-submit-subtitle')) {
        const subtitle = root.ownerDocument.createElement('small');
        subtitle.className = 'chloe-cod-submit-subtitle'; subtitle.textContent = 'Pagaras al recibir tu producto';
        button.append(subtitle);
      }
    } else button.querySelector('.chloe-cod-submit-subtitle')?.remove();
  }
}

export function installNativeSubmitGuard(root, config, catalog, signal, {isOpening} = {}) {
  const doc = root.ownerDocument, marker = 'data-chloe-submit-disabled', states = new Map();
  let stopped = false;
  const active = () => {
    try { nativeContext(root, config); return BODY_OWNERS.get(doc.body) === root; } catch { return false; }
  };
  const busy = button => button.getAttribute('aria-disabled') === 'true' || button.getAttribute('aria-busy') === 'true' || (button.hasAttribute('data-loading') && button.getAttribute('data-loading') !== 'false') || /(?:^|[\s_-])(loading|busy|processing|submitting)(?:[\s_-]|$)/i.test(String(button.className || ''));
  const ready = () => active() && !isOpening?.() && root.dataset.nativeSelectionReadback === 'verified' && Boolean(readNativeSelection(root, config, catalog));
  const disabledObserver = new MutationObserver(records => { recordMutations(records); refresh(); });
  disabledObserver.observe(doc.body, {subtree: true, attributes: true, attributeFilter: ['disabled']});
  const drain = () => recordMutations(disabledObserver.takeRecords());
  const writeDisabled = (button, state, value) => {
    drain();
    if (button.disabled === value) return;
    button.disabled = value;
    // Drain immediately: this native boolean property write is synchronous.
    // Only this isolated write's record is ours; all earlier records were app writes.
    recordMutations(disabledObserver.takeRecords().filter(record => record.target !== button || record.attributeName !== 'disabled'));
  };
  function recordMutations(records) {
    for (const record of records) {
      if (record.type !== 'attributes' || record.attributeName !== 'disabled') continue;
      const state = states.get(record.target);
      if (!state) continue;
      state.appDisabled = record.target.disabled;
    }
  }
  function refresh() {
    if (stopped || !active()) return;
    drain();
    const modals = [...doc.querySelectorAll(OPEN_MODAL_SELECTOR)], confirmed = ready();
    for (const modal of modals) {
      const buttons = [...modal.querySelectorAll('button._rsi-modal-submit-button')];
      for (const button of buttons) {
        if (!visibleControl(button)) continue;
        let state = states.get(button);
        if (!state) { state = {appDisabled: button.disabled, owned: false}; states.set(button, state); }
        const form = modal.querySelector('form#_rsi-cod-form-modal-form');
        if (confirmed && buttons.length === 1 && form && button.form === form) {
          if (!state.owned || busy(button)) continue;
          state.owned = false; button.removeAttribute(marker);
          if (!state.appDisabled) writeDisabled(button, state, false);
        } else if (!button.disabled) {
          state.appDisabled = false; state.owned = true;
          button.setAttribute(marker, TARGET_PRODUCT_ID); writeDisabled(button, state, true);
        }
      }
    }
  }
  function intercept(event) {
    if (stopped || !active()) return;
    const modal = event.target?.closest?.(OPEN_MODAL_SELECTOR);
    if (!modal || (event.type !== 'submit' && !event.target.closest?.('button._rsi-modal-submit-button'))) return;
    const buttons = [...modal.querySelectorAll('button._rsi-modal-submit-button')];
    const identities = readNativeOfferIdentities(root);
    const correctOwner = identities?.modal === modal && (event.type === 'submit' ? event.target === identities.form : event.target.closest('button._rsi-modal-submit-button')?.form === identities.form);
    refresh();
    if (!correctOwner || !ready() || buttons.length !== 1 || buttons[0].disabled || busy(buttons[0])) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  }
  function cleanup() {
    if (stopped) return; stopped = true;
    drain();
    doc.defaultView.removeEventListener('click', intercept, true); doc.defaultView.removeEventListener('submit', intercept, true);
    for (const [button, state] of states) {
      if (!state.owned || button.getAttribute(marker) !== TARGET_PRODUCT_ID) continue;
      button.removeAttribute(marker);
      if (!state.appDisabled && !busy(button)) button.disabled = false;
    }
    disabledObserver.disconnect(); states.clear();
  }
  doc.defaultView.addEventListener('click', intercept, true); doc.defaultView.addEventListener('submit', intercept, true);
  signal?.addEventListener('abort', cleanup, {once: true});
  return {refresh, flush: drain, cleanup};
}

function observeNativePresentation(root, config, signal, submitGuard) {
  const doc = root.ownerDocument;
  let queued = false;
  const refresh = () => {
    if (queued || signal.aborted) return; queued = true;
    queueMicrotask(() => { queued = false; if (!signal.aborted) { refreshNativePresentation(root, config); submitGuard?.refresh(); } });
  };
  const observer = new MutationObserver(refresh);
  observer.observe(doc.body, {subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'style', 'disabled', 'aria-disabled', 'aria-busy', 'data-loading', 'data-order-total', 'hidden'], characterData: true});
  signal.addEventListener('abort', () => observer.disconnect(), {once: true});
  refresh();
}

function formatMoney(total) { return '$' + new Intl.NumberFormat('es-CO', {maximumFractionDigits: 0}).format(total); }

function installImageZoom(root, signal) {
  const dialog = root.querySelector('#chloe-image-dialog'), image = dialog?.querySelector('[data-chloe-zoom-image]');
  if (!dialog || !image) return;
  let returnFocus = null;
  root.addEventListener('click', event => {
    const trigger = event.target.closest?.('[data-chloe-zoom]');
    if (trigger && root.contains(trigger)) {
      event.preventDefault();
      const original = trigger.matches('img') ? trigger : trigger.querySelector('img');
      if (original) { image.src = original.currentSrc || original.src; image.alt = original.alt; }
      returnFocus = trigger; if (!dialog.open) dialog.showModal();
      dialog.querySelector('[data-chloe-close-image],button')?.focus();
    }
    if (event.target.closest?.('[data-chloe-close-image]')) dialog.close();
  }, {signal});
  dialog.addEventListener('close', () => returnFocus?.focus(), {signal});
  dialog.addEventListener('click', event => {
    if (event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
  }, {signal});
  signal.addEventListener('abort', () => { if (dialog.open) dialog.close(); }, {once: true});
}

function installReviewCarousel(root, signal) {
  for (const carousel of root.querySelectorAll('[data-chloe-reviews]')) {
    const track = carousel.querySelector('.splide__track');
    const slides = [...carousel.querySelectorAll('.splide__list > .splide__slide')];
    const arrows = carousel.querySelector('.splide__arrows');
    if (!track || slides.length < 2 || !arrows) continue;
    const doc = root.ownerDocument;
    const motion = doc.defaultView.matchMedia?.('(prefers-reduced-motion: reduce)');
    track.tabIndex = 0;
    track.setAttribute('aria-label', 'Opiniones del producto. Desliza o usa las flechas para leerlas.');
    const currentIndex = () => {
      const origin = slides[0].offsetLeft;
      return slides.reduce((best, slide, index) => Math.abs(slide.offsetLeft - origin - track.scrollLeft) < Math.abs(slides[best].offsetLeft - origin - track.scrollLeft) ? index : best, 0);
    };
    const move = direction => {
      const index = (currentIndex() + direction + slides.length) % slides.length;
      track.scrollTo({left: slides[index].offsetLeft - slides[0].offsetLeft, behavior: motion?.matches ? 'instant' : 'smooth'});
    };
    for (const [name, direction, label] of [['prev', -1, 'Opinión anterior'], ['next', 1, 'Opinión siguiente']]) {
      let button = arrows.querySelector('[data-chloe-review-' + name + ']');
      if (!button) {
        button = doc.createElement('button'); button.type = 'button'; button.setAttribute('data-chloe-review-' + name, ''); button.setAttribute('aria-label', label);
        const svg = doc.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('aria-hidden', 'true');
        const arrow = doc.createElementNS('http://www.w3.org/2000/svg', 'path');
        arrow.setAttribute('d', direction < 0 ? 'm14.7 5.3-1.4-1.4L5.2 12l8.1 8.1 1.4-1.4L8 12z' : 'm9.3 5.3 1.4-1.4 8.1 8.1-8.1 8.1-1.4-1.4L16 12z');
        svg.append(arrow); button.append(svg); arrows.append(button);
      }
      button.addEventListener('click', () => move(direction), {signal});
    }
    track.addEventListener('keydown', event => {
      if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault(); move(event.key === 'ArrowLeft' ? -1 : 1);
    }, {signal});
    track.addEventListener('scroll', () => { carousel.dataset.chloeReviewIndex = String(currentIndex() + 1); }, {passive: true, signal});
    carousel.dataset.chloeReviewIndex = '1';
  }
}

export function routePurchaseClick(event, root, {nativeMode, onNative, onReview}) {
  const owner = event.target.closest?.(OWNER_SELECTOR);
  if (nativeMode && owner && root.contains(owner)) {
    // The app's actual owner click must reach its own listeners unchanged.
    void onNative(false);
    return 'native-owner';
  }
  const button = event.target.closest?.('[data-chloe-open-cod]');
  if (!button || !root.contains(button)) return null;
  event.preventDefault();
  if (nativeMode) {
    // Only the page CTA's outer click stops here. Its nested owner.click() still
    // propagates normally, so generic cart/EasySell compatibility routes cannot
    // replace the product-specific modal with a cart modal after it opens.
    event.stopPropagation();
    void onNative(true);
    return 'native-delegate';
  }
  onReview();
  return 'review';
}

export function routeSupplementalWindowCapture(event, root, onNative) {
  // Window capture precedes document capture, where a compatibility listener
  // can otherwise start a cart request before the section sees the click.
  // Actual owner events, including the delegated owner.click(), stay untouched.
  const owner = event.target.closest?.(OWNER_SELECTOR);
  if (owner && root.contains(owner)) return null;
  const button = event.target.closest?.('[data-chloe-open-cod]');
  if (!button || !root.contains(button)) return null;
  event.stopImmediatePropagation();
  return routePurchaseClick(event, root, {nativeMode: true, onNative, onReview() {}});
}

export function clickNativeOwnerCopy(root) {
  const owners = [...root.querySelectorAll(OWNER_SELECTOR)];
  if (owners.length !== 1) throw Error('Native owner unavailable');
  const owner = owners[0];
  if (!root.contains(owner) || !visibleControl(owner) || owner.matches('[data-chloe-open-cod]') || owner.disabled || owner.getAttribute('aria-disabled') === 'true' || root.ownerDocument.defaultView.getComputedStyle(owner).pointerEvents === 'none') throw Error('Native owner unavailable');
  const candidates = [...owner.children].filter(node => node.tagName === 'SPAN' && node.parentElement === owner && owner.contains(node) && node.closest(OWNER_SELECTOR) === owner && visibleControl(node) && root.ownerDocument.defaultView.getComputedStyle(node).pointerEvents !== 'none' && [...node.childNodes].some(child => child.nodeType === 3 && child.textContent.trim()));
  const preferred = candidates.filter(node => node.querySelector('._rsi-buy-now-button-subtitle'));
  const copies = preferred.length ? preferred : candidates;
  if (copies.length !== 1) throw Error('Native owner copy unavailable');
  // Dispatch through the visible copy of the real app control. The app's native
  // product listener receives the ordinary bubbling event unchanged.
  copies[0].click();
  return copies[0];
}

export function routeNativeOwnerKeydown(event, root, onNative) {
  if (event.repeat || !['Enter', ' '].includes(event.key)) return null;
  const owner = event.target.closest?.(OWNER_SELECTOR);
  if (!owner || !root.contains(owner) || owner.matches('button,a,input')) return null;
  event.preventDefault(); void onNative(true);
  return 'native-owner-keydown';
}

export function routeNativeCloseFenceCapture(event, root, fence, onNative) {
  const owner = event.target.closest?.(OWNER_SELECTOR);
  if (!owner || !root.contains(owner) || !fence?.blocked()) return null;
  event.preventDefault(); event.stopImmediatePropagation(); void onNative(true);
  return 'native-restore-wait';
}

export function initChloeMobile(root) {
  if (CONTROLLERS.has(root)) return CONTROLLERS.get(root);
  const doc = root.ownerDocument, abort = new AbortController(), signal = abort.signal;
  const section = root.closest('.shopify-section');
  let removeScope = () => {}, readback = null, submitGuard = null, closeFence = null, openingNative = false, lastNativeSnapshot = null, destroyed = false;
  const controller = {destroy() { if (destroyed) return; destroyed = true; abort.abort(); readback?.cleanup(); submitGuard?.cleanup(); closeFence?.cleanup(); clearNativeOfferPresentation(root); removeScope(); CONTROLLERS.delete(root); delete root.chloeMobile; if (root.dataset.chloeInitialized !== 'failed') delete root.dataset.chloeInitialized; }};
  CONTROLLERS.set(root, controller);
  section?.addEventListener('shopify:section:unload', controller.destroy, {once: true, signal});
  function notice(text, failed = false) {
    const element = root.querySelector('[data-chloe-purchase-status]');
    if (!element) return; element.hidden = !text; element.textContent = text;
    if (failed) element.focus();
  }
  try {
    const configs = root.querySelectorAll('script[type="application/json"][data-chloe-config]');
    if (configs.length !== 1 || !root.matches('.chloe-mobile[id^="chloe-mobile-"]')) throw Error('Missing Chloe root contract');
    const config = JSON.parse(configs[0].textContent);
    const mode = resolvePurchaseMode(config, root.dataset.productId), nativeMode = mode === 'native';
    const catalog = validateCatalog(config, mode);
    const pagePackage = readPagePackage(root, config), radios = pagePackage.radios;
    let quantity = pagePackage.quantity, pageRevision = 0;
    const available = catalog.variants.filter(variant => variant.available);
    if (!available.length) throw Error('No Chloe variants available');
    const preferred = ['Palo De Rosa', 'Negro', 'Beige', 'Azul Bebe', 'Gris Claro', 'Azul Oscuro'];
    const selections = preferred.map(color => { const variant = available.find(item => item.size === 'M' && item.color === color) || available[0]; return {size: variant.size, color: variant.color}; });
    const nativeDialog = root.querySelector('#mobile-cod-dialog');
    if (nativeMode && nativeDialog) {
      if (nativeDialog.open) nativeDialog.close(); nativeDialog.hidden = true; nativeDialog.inert = true;
      nativeDialog.querySelectorAll('input,select,textarea,button').forEach(element => { element.disabled = true; });
    }
    function updatePage() {
      const order = calculateOrder(quantity, selections);
      for (const radio of radios) {
        radio.checked = Number(radio.value) === quantity;
        const label = root.querySelector('label[for="' + radio.id + '"]');
        if (!label) continue;
        const price = label.querySelector('.quantity-break__price > span') || label.querySelector('.quantity-break__price');
        const total = Number(radio.value) === quantity ? order.total : PACKAGE_PRICES[radio.value];
        if (price && price.textContent !== formatMoney(total)) price.textContent = formatMoney(total);
      }
      root.dataset.selectedQuantity = String(quantity); root.dataset.selectedTotal = String(order.total);
      root.querySelectorAll('[data-selected-quantity]').forEach(element => { element.textContent = String(quantity); });
      root.querySelectorAll('[data-chloe-total]').forEach(element => { element.textContent = formatMoney(order.total); });
      doc.dispatchEvent(new doc.defaultView.CustomEvent('chloe:selection', {detail: {productId: TARGET_PRODUCT_ID, quantity, total: order.total, verified: root.dataset.nativeSelectionReadback === 'verified'}}));
    }
    function acceptNativeSelection(snapshot) {
      if (closeFence && !['opening', 'open'].includes(closeFence.state())) return;
      lastNativeSnapshot = snapshot; quantity = snapshot.quantity;
      snapshot.items.forEach((item, index) => { selections[index] = {...item}; });
      closeFence?.verified(readNativeOfferIdentities(root)?.modal);
      root.dataset.nativeSelectionReadback = 'verified'; updatePage(); notice(''); submitGuard?.refresh();
    }
    async function synchronizeOpening() {
      // A page change that arrives during slow app loading has precedence.
      for (let attempt = 0; attempt < 4; attempt++) {
        const revision = pageRevision, requestedQuantity = quantity;
        const items = selections.slice(0, requestedQuantity).map(item => ({...item}));
        await selectNativePackage(root, config, requestedQuantity, {timeoutMs: 45000, signal});
        const snapshot = lastNativeSnapshot ? await restoreNativeSelection(root, config, catalog, requestedQuantity, items, {signal}) : await waitForNativeDom(root, config, () => { const current = readNativeSelection(root, config, catalog); return current?.quantity === requestedQuantity ? current : null; }, 45000, signal);
        if (revision !== pageRevision) continue;
        acceptNativeSelection(snapshot); return;
      }
      throw Error('Chloe page selection changed during synchronization');
    }
    async function openNative(invokeOwner) {
      if (openingNative || destroyed) return;
      openingNative = true; root.dataset.nativeSelectionReadback = 'pending'; submitGuard?.refresh();
      try {
        if (closeFence?.blocked()) { notice('Preparando tu pedido…'); await closeFence.wait(); }
        if (destroyed || signal.aborted) return;
        closeFence?.opening();
        if (invokeOwner) {
          clickNativeOwnerCopy(root);
        }
        await synchronizeOpening();
      } catch (error) {
        if (['opening', 'open'].includes(closeFence?.state())) closeFence.openingFailed();
        if (!signal.aborted) { root.dataset.nativeSelectionReadback = 'failed'; notice(closeFence?.state() === 'failed' ? 'Vuelve a cargar la página para continuar.' : 'No se pudo confirmar tu selección. Revisa las tallas y colores en el formulario o recarga la página.', true); submitGuard?.refresh(); }
      } finally { openingNative = false; if (!signal.aborted) readback?.refresh(true); }
    }
    root.addEventListener('change', event => {
      if (!radios.includes(event.target) || !event.target.checked) return;
      quantity = Number(event.target.value); pageRevision++;
      root.dataset.nativeSelectionReadback = nativeMode ? 'pending' : 'review'; updatePage();
    }, {signal});
    if (nativeMode) doc.defaultView.addEventListener('click', event => {
      if (routeNativeCloseFenceCapture(event, root, closeFence, openNative)) return;
      routeSupplementalWindowCapture(event, root, openNative);
    }, {capture: true, signal});
    root.addEventListener('click', event => routePurchaseClick(event, root, {
      nativeMode, onNative: openNative,
      onReview() {
        if (nativeDialog && !nativeDialog.open) nativeDialog.showModal();
        notice('Vista previa: no se crea ni se envía ningún pedido.');
        root.dispatchEvent(new doc.defaultView.CustomEvent('chloe:review-open', {bubbles: true, detail: {quantity, ...calculateOrder(quantity, selections)}}));
      }
    }), {capture: true, signal});
    root.addEventListener('keydown', event => {
      if (nativeMode) routeNativeOwnerKeydown(event, root, openNative);
    }, {signal});
    if (nativeMode) {
      removeScope = installNativeScope(root, config);
      closeFence = installNativeCloseFence(root, signal);
      submitGuard = installNativeSubmitGuard(root, config, catalog, signal, {isOpening: () => openingNative || Boolean(closeFence?.blocked())});
      observeNativePresentation(root, config, signal, submitGuard);
      readback = observeNativeSelection(root, config, catalog, {isOpening: () => openingNative, onSelection: acceptNativeSelection, signal});
      root.dataset.nativeQuantitySync = 'pending'; root.dataset.nativeSelectionReadback = 'pending';
    } else if (nativeDialog) {
      // Local visual review has no order endpoint, including Enter-key submission.
      nativeDialog.addEventListener('submit', event => { event.preventDefault(); notice('Vista previa: no se crea ni se envía ningún pedido.'); }, {capture: true, signal});
      nativeDialog.addEventListener('click', event => { if (event.target.closest?.('[data-close-cod]')) nativeDialog.close(); if (event.target.closest?.('#cod-submit')) { event.preventDefault(); notice('Vista previa: no se crea ni se envía ningún pedido.'); } }, {signal});
    }
    installImageZoom(root, signal);
    installReviewCarousel(root, signal);
    root.dataset.purchaseMode = mode; root.dataset.chloeInitialized = 'true';
    root.chloeMobile = Object.freeze({productId: TARGET_PRODUCT_ID, dropiProductId: TARGET_DROPI_ID, purchaseMode: mode, getSelection() { return {...calculateOrder(quantity, selections), items: selections.slice(0, quantity).map(item => { const variant = catalog.variants.find(value => value.size === item.size && value.color === item.color); return {...item, shopifyVariantId: variant.variantId, dropiVariantId: variant.dropiVariantId, available: variant.available, quantity: 1}; }), nativeVerified: Boolean(lastNativeSnapshot && lastNativeSnapshot.quantity === quantity && lastNativeSnapshot.items.every((item, index) => item.size === selections[index].size && item.color === selections[index].color))}; }});
    updatePage();
  } catch (error) {
    root.dataset.chloeInitialized = 'failed';
    root.querySelectorAll('[data-chloe-open-cod]').forEach(button => { button.disabled = true; });
    notice('No se pudo validar este producto. Recarga la página e inténtalo de nuevo.');
    console.error('[CosaUtil Chloe mobile]', error.message);
    controller.destroy();
  }
  return controller;
}

function initializeWithin(scope) {
  if (scope.matches?.('.chloe-mobile[id^="chloe-mobile-"]')) initChloeMobile(scope);
  for (const root of scope.querySelectorAll('.chloe-mobile[id^="chloe-mobile-"]')) initChloeMobile(root);
}

if (typeof document !== 'undefined') {
  initializeWithin(document);
  document.addEventListener('shopify:section:load', event => initializeWithin(event.target));
}
